<template>
  <div class="task-item" :class="`task-${task.status}`">
    <!-- 任务信息区域 -->
    <div class="task-info">
      <!-- 文件图标 -->
      <div class="file-icon">
        <Icon :name="getFileIcon(task.originalFileName)" size="24" />
      </div>

      <!-- 文件详情 -->
      <div class="file-details">
        <h4 class="file-name">{{ task.originalFileName }}</h4>
        <p class="translation-info">
          {{ $t('taskItem.to') }}<span class="target-language">{{ targetLanguageName }}</span>
        </p>
        <p class="created-time">{{ formatTime(task.createdAt) }}</p>
      </div>
    </div>

    <!-- 状态指示区域 -->
    <div class="status-indicator">
      <!-- 上传中状态 -->
      <div v-if="task.status === 'uploading'" class="status-uploading">
        <Icon name="mdi:loading" size="16" class="rotating" />
        <span class="status-text">{{ $t('taskItem.status.uploading') }}</span>
      </div>

      <!-- 翻译中状态 -->
      <div v-else-if="task.status === 'translating'" class="status-translating">
        <Icon name="mdi:loading" size="16" class="rotating" />
        <span class="status-text">{{ $t('taskItem.status.translating') }}</span>
        <el-progress
          v-if="task.progress !== null"
          :percentage="task.progress"
          :stroke-width="6"
          :show-text="true"
          class="translate-progress"
        />
      </div>

      <!-- 完成状态 -->
      <div v-else-if="task.status === 'completed'" class="status-completed">
        <Icon name="material-symbols:check-circle" size="16" />
        <span class="status-text">{{ $t('taskItem.status.completed') }}</span>
        <p class="completed-time">{{ formatTime(task.createdAt, true) }}</p>
      </div>

      <!-- 失败状态 -->
      <div v-else-if="task.status === 'failed'" class="status-failed">
        <Icon name="material-symbols:error" size="16" />
        <span class="status-text">{{ $t('taskItem.status.failed') }}</span>
        <p class="error-message">{{ task.error }}</p>
      </div>
    </div>

    <!-- 操作区域 -->
    <div class="task-actions">
      <!-- 下载按钮（完成状态） -->
      <button
        v-if="task.status === 'completed'"
        class="action-button download-button"
        @click="handleDownload"
      >
        <Icon name="material-symbols:download" size="16" />
        {{ $t('taskItem.actions.download') }}
      </button>

      <!-- 重试按钮（失败状态） -->
      <button
        v-if="task.status === 'failed'"
        class="action-button retry-button"
        @click="handleRetry"
      >
        <Icon name="material-symbols:refresh" size="16" />
        {{ $t('taskItem.actions.retry') }}
      </button>

      <!-- 删除按钮（仅完成或失败状态） -->
      <button
        v-if="task.status === 'completed' || task.status === 'failed'"
        class="action-button delete-button"
        @click="handleDelete"
      >
        <Icon name="material-symbols:delete-outline" size="16" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
// @nuxt/icon 模块已自动提供 <Icon> 组件，无需手动导入
import { computed } from 'vue'
import type { Task } from '~/types/translation'
import { useLanguageDisplay } from '~/composables/useLanguageDisplay'

// Props
const props = defineProps<{
  task: Task
}>()

// Emits
const emit = defineEmits<{
  (e: 'delete', taskId: string): void
  (e: 'download', task: Task): void
  (e: 'retry', taskId: string): void
}>()

// 获取文件图标
const getFileIcon = (fileName: string): string => {
  const ext = fileName.split('.').pop()?.toLowerCase()

  switch (ext) {
    case 'pdf':
      return 'vscode-icons:file-type-pdf2'
    case 'docx':
    case 'doc':
      return 'vscode-icons:file-type-word2'
    case 'xlsx':
    case 'xls':
      return 'vscode-icons:file-type-excel2'
    case 'pptx':
    case 'ppt':
      return 'vscode-icons:file-type-powerpoint2'
    case 'epub':
      return 'material-symbols:book-2'
    case 'srt':
    case 'vtt':
      return 'material-symbols:subtitles'
    default:
      return 'material-symbols:description'
  }
}

// 格式化时间
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
const formatTime = (timeStr: string, showTime = false): string => {
  const date = new Date(timeStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (showTime) {
    // 完成时间显示
    if (diffMinutes < 1) return t('taskItem.time.justNowDone')
    if (diffMinutes < 60) return t('taskItem.time.minutesAgoDone', { n: diffMinutes })
    if (diffHours < 24) return t('taskItem.time.hoursAgoDone', { n: diffHours })
    return t('taskItem.time.daysAgoDone', { n: diffDays })
  } else {
    // 创建时间显示
    if (diffMinutes < 1) return t('taskItem.time.justNow')
    if (diffMinutes < 60) return t('taskItem.time.minutesAgo', { n: diffMinutes })
    if (diffHours < 24) return t('taskItem.time.hoursAgo', { n: diffHours })
    return t('taskItem.time.daysAgo', { n: diffDays })
  }
}

// 事件处理：向父组件发射事件
const handleDownload = () => {
  if (props.task.status === 'completed') emit('download', props.task)
}

const handleRetry = () => {
  if (props.task.status === 'failed') emit('retry', props.task.id)
}

const handleDelete = () => {
  emit('delete', props.task.id)
}

// 映射语言代码为可读名称
const { codeToName } = useLanguageDisplay()
const targetLanguageName = computed(() => codeToName(props.task.targetLanguage))
</script>

<style scoped lang="scss">
@use "sass:color";

.task-item {
  display: flex;
  align-items: center;
  gap: $spacing-md;
  padding: $spacing-md;
  background: $white;
  border: 1px solid $gray-200;
  border-radius: $border-radius-md;
  margin-bottom: $spacing-sm;
  transition: all 0.2s ease;

  &:hover {
    border-color: $gray-300;
    box-shadow: $shadow-sm;
    transform: translateY(-1px);
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.task-info {
  display: flex;
  align-items: center;
  gap: $spacing-sm;
  flex: 1;
  min-width: 0; // 允许内容收缩
}

.file-icon {
  flex-shrink: 0;
}

.file-details {
  flex: 1;
  min-width: 0;

  .file-name {
    font-size: 0.95rem;
    font-weight: 600;
    color: $gray-800;
    margin: 0 0 $spacing-xs 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .translation-info {
    font-size: 0.85rem;
    color: $gray-600;
    margin: 0 0 $spacing-xs 0;

    .target-language {
      font-weight: 500;
      color: $primary-color;
    }
  }

  .created-time {
    font-size: 0.8rem;
    color: $gray-500;
    margin: 0;
  }
}

.status-indicator {
  flex-shrink: 0;
  min-width: 120px;
  text-align: center;
}

// 状态样式
.status-uploading,
.status-translating,
.status-completed,
.status-failed {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $spacing-xs;

  .status-text {
    font-size: 0.85rem;
    font-weight: 500;
  }
}

.status-uploading {
  .status-text {
    color: $info;
  }
}

.status-translating {
  .status-text {
    color: $warning;
  }

  .translate-progress {
    width: 100%;
    margin-top: $spacing-xs;
  }
}

.status-completed {
  .status-text {
    color: $success;
  }

  .completed-time {
    font-size: 0.75rem;
    color: $gray-500;
    margin: 0;
  }
}

.status-failed {
  .status-text {
    color: $danger;
  }

  .error-message {
    font-size: 0.75rem;
    color: $danger;
    margin: 0;
    text-align: center;
    line-height: 1.3;
  }
}

// 旋转动画
.rotating {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

// 去除进度条相关动画（无需进度条）

.task-actions {
  display: flex;
  gap: $spacing-xs;
  flex-shrink: 0;
}

// 通用小字提示样式
  .status-hint-base {
    margin: 0;
    font-size: 12px;
    color: $gray-500;
    a.hint-link {
      color: $primary-color;
      text-decoration: underline;
    }
  }

.action-button {
  display: flex;
  align-items: center;
  gap: $spacing-xs;
  padding: $spacing-xs $spacing-sm;
  border: none;
  border-radius: $border-radius-sm;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &.download-button {
    background-color: $success;
    color: $white;

    &:hover {
      background-color: color.adjust($success, $lightness: -10%);
    }
  }

  &.retry-button {
    background-color: $warning;
    color: $white;

    &:hover {
      background-color: color.adjust($warning, $lightness: -10%);
    }
  }

  &.cancel-button {
    background-color: $gray-500;
    color: $white;

    &:hover {
      background-color: color.adjust($gray-500, $lightness: -10%);
    }
  }

  &.delete-button {
    background-color: transparent;
    color: $gray-400;
    padding: $spacing-xs;

    &:hover {
      background-color: $gray-100;
      color: $danger;
    }
  }
}

// 任务状态对应的左边框颜色
.task-uploading {
  border-left: 4px solid $info;
}

.task-translating {
  border-left: 4px solid $warning;
}

.task-completed {
  border-left: 4px solid $success;
}

.task-failed {
  border-left: 4px solid $danger;
}

// 响应式设计
@media (max-width: 768px) {
  .task-item {
    flex-direction: column;
    align-items: stretch;
    gap: $spacing-sm;
  }

  .task-info {
    flex-direction: column;
    text-align: center;
  }

  .file-details {
    .file-name {
      white-space: normal;
      line-height: 1.3;
    }
  }

  .status-indicator {
    min-width: auto;
  }

  .task-actions {
    justify-content: center;
    flex-wrap: wrap;
  }
}
</style>
